// The in-world direction picker: four cardinal arrows drawn around a square,
// clicked to choose a facing.
//
// It replaces a N/E/S/W toggle in the chrome, and the reason is not tidiness.
// That toggle was a *mode* carried between actions: it defaulted to North, it
// was shared with belt laying, and nothing tied it to the thing being aimed.
// Re-aiming a north-facing pawn to north is legal, costs the whole turn and
// changes nothing on the board — so the commonest slip was invisible. Asking
// for the direction at the moment of the click, on the square being aimed,
// makes the choice explicit and local.
//
// Drawn with depthTest disabled, unlike the pawn chevron since the walker
// set: at a 3/4 pitch a floor marker cannot clear the piece standing on its
// own square (the arithmetic is in chevron.ts), and these are a control — they
// must be visible and clickable wherever they appear, where a chevron hidden
// behind its pawn is the piece's own front doing the job. Raycasting is
// unaffected by depthTest, so picking still works normally.

import {
  type BufferGeometry,
  type Camera,
  Color,
  DoubleSide,
  ExtrudeGeometry,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Raycaster,
  Shape,
  Vector2,
  Vector3,
  CircleGeometry
} from "three";

import type { Direction } from "src/shared/board";
import { PALETTE } from "./palette";
import { FACING_YAW, squareToWorld } from "./coords";

/** Fractions of a square. Big enough to be a comfortable pointer target. */
const WIDTH = 0.42;
const DEPTH = 0.34;
const THICKNESS = 0.04;
/** Centre-to-tip distance: out past the piece, still inside the neighbour. */
const RADIUS = 0.86;
/** Above the pieces, so the arrows read as an overlay rather than as scenery. */
const PICKER_RENDER_ORDER = 1000;
const LIFT = 0.42;

function arrowShape(): Shape {
  const shape = new Shape();
  const halfWidth = WIDTH / 2;
  const tail = DEPTH * 0.35;

  // Same XY-to-world mapping as the chevron: after rotateX(-PI/2), shape +Y
  // becomes world -Z, which is north. The tip therefore belongs at +DEPTH/2.
  shape.moveTo(0, DEPTH / 2);
  shape.lineTo(halfWidth, -DEPTH / 2 + tail);
  shape.lineTo(halfWidth * 0.34, -DEPTH / 2 + tail);
  shape.lineTo(halfWidth * 0.34, -DEPTH / 2);
  shape.lineTo(-halfWidth * 0.34, -DEPTH / 2);
  shape.lineTo(-halfWidth * 0.34, -DEPTH / 2 + tail);
  shape.lineTo(-halfWidth, -DEPTH / 2 + tail);
  shape.closePath();
  return shape;
}

function createArrowGeometry(): BufferGeometry {
  const geometry = new ExtrudeGeometry(arrowShape(), {
    depth: THICKNESS,
    bevelEnabled: false
  });
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(0, 0, -RADIUS);
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Four arrows parented to one group. The group moves to the square being
 * aimed; each arrow is pre-rotated to its own compass direction, so the
 * direction a click means is the arrow's index — no angle maths at pick time.
 */
/** What `pick` returns for the centre target: not a direction, a removal. */
export const DIRECTION_REMOVE = 4;

export class DirectionPicker {
  readonly group = new Object3D();

  private arrows: Mesh[] = [];
  /**
   * A centre target, shown only when the thing being pointed at is something
   * that can also be taken away — a belt. Removal belongs *here*, at the
   * player's cursor on the part itself, rather than as a button in a bar at
   * the bottom of the screen: that is where they clicked and what they are
   * looking at.
   */
  private centre?: Mesh;
  private raycaster = new Raycaster();
  private pointer = new Vector2();
  private disposables: { dispose(): void }[] = [];
  private square: number | null = null;

  constructor() {
    const geometry = createArrowGeometry();
    this.disposables.push(geometry);

    for (let dir = 0; dir < 4; dir++) {
      const material = new MeshBasicMaterial({
        color: new Color(PALETTE.picker),
        // The gameplay-overlay treatment: never occluded, never lit, so it
        // reads identically over a tile, a piece or a belt.
        depthTest: false,
        depthWrite: false,
        transparent: true,
        opacity: 0.95,
        side: DoubleSide
      });
      const mesh = new Mesh(geometry, material);
      mesh.rotation.y = FACING_YAW[dir as Direction];
      mesh.renderOrder = PICKER_RENDER_ORDER;
      mesh.userData.direction = dir;
      mesh.visible = false;
      this.arrows.push(mesh);
      this.group.add(mesh);
      this.disposables.push(material);
    }

    // A disc in the middle of the four arrows: "take this away". Distinct in
    // shape as well as position, so it is not mistaken for a fifth direction.
    //
    // Filled, not a ring. A ring is the better-looking symbol and it is the
    // wrong *hit* shape: an annulus has no geometry in the middle, so the
    // raycast went straight through the hole the player was aiming at and the
    // control did nothing when clicked dead centre.
    const ringGeometry = new CircleGeometry(0.2, 20).rotateX(-Math.PI / 2);
    const ringMaterial = new MeshBasicMaterial({
      color: new Color(PALETTE.capture),
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 0.95,
      side: DoubleSide
    });
    this.centre = new Mesh(ringGeometry, ringMaterial);
    this.centre.renderOrder = PICKER_RENDER_ORDER;
    this.centre.userData.direction = DIRECTION_REMOVE;
    this.centre.visible = false;
    this.group.add(this.centre);
    this.disposables.push(ringGeometry, ringMaterial);
  }

  /**
   * Show the picker on a square, or pass null to hide it.
   *
   * `removable` adds the centre target. `DIRECTION_REMOVE` is what `pick`
   * returns for it.
   *
   * `scale` is the whole control, not the arrows: the group carries the
   * arrows' offset from the centre as well as their size, so scaling it moves
   * them apart and grows them together and nothing has to be scaled twice.
   * `arrowCentre` reads the mesh through `localToWorld` and picking raycasts
   * the meshes, so both follow for free.
   */
  show(square: number | null, removable = false, scale = 1): void {
    this.square = square;
    const visible = square !== null;
    for (const arrow of this.arrows) arrow.visible = visible;
    if (this.centre) this.centre.visible = visible && removable;
    if (square === null) return;
    this.group.scale.setScalar(scale);
    squareToWorld(square, LIFT, this.group.position as Vector3);
  }

  get shownAt(): number | null {
    return this.square;
  }

  /**
   * World-space centre of one arrow, or null when the picker is closed. The
   * arrow's geometry is offset along its own -Z and then yawed, so this asks
   * the mesh rather than recomputing the trigonometry and risking a copy that
   * drifts from what is actually drawn.
   */
  arrowCentre(direction: number): Vector3 | null {
    const arrow = this.arrows[direction];
    if (this.square === null || !arrow) return null;
    this.group.updateMatrixWorld(true);
    return arrow.localToWorld(new Vector3(0, 0, -RADIUS));
  }

  /**
   * Which arrow is under a normalised device point, or -1. Callers must
   * consult this *before* square picking: the arrows hang over neighbouring
   * tiles, so the same click resolves to both.
   */
  pick(ndcX: number, ndcY: number, camera: Camera): number {
    if (this.square === null) return -1;
    this.pointer.set(ndcX, ndcY);
    this.raycaster.setFromCamera(this.pointer, camera);
    const targets = this.centre?.visible ? [...this.arrows, this.centre] : this.arrows;
    const hits = this.raycaster.intersectObjects(targets, false);
    const hit = hits[0]?.object;
    return hit ? (hit.userData.direction as number) : -1;
  }

  dispose(): void {
    for (const item of this.disposables) item.dispose();
    this.disposables = [];
    this.arrows = [];
  }
}
